import styled, { css } from 'styled-components';
import { themeToken } from '../../utils/cpds-utils';
import { cpdsMediaQueryVals } from '../../token/shared/common';
import { CpdsCardFigure } from './card-figure';

//typings
import { CpdsCardComponentProps } from '../typings';
import { checkValue } from 'src/components/common/helper';

const getCardWidth = (sideSpacing = 56, gridGrap = 40, cols = 12) => {
  const spacing = `${sideSpacing * 2}px`; // page side spacing
  const gap = `${gridGrap * (cols - 1)}px`; // gap between spacing grid columns
  return {
    '3_COLUMNS': `calc((((100vw - ${spacing} - ${gap}) / ${cols}) * 3) + ${gridGrap * 2}px)`,
    '4_COLUMNS': `calc((((100vw - ${spacing} - ${gap}) / ${cols}) * 4) + ${gridGrap * 3}px)`,
    '6_COLUMNS': `calc((((100vw - ${spacing} - ${gap}) / ${cols}) * 6) + ${gridGrap * 5}px)`,
    FUll_WIDTH: 'none',
  };
};

const IMAGE_WIDTH = {
  ARTICLE: {
    desktop: { width: '98px', ratio: '1/1' },
    mobile: { width: '105px', ratio: '3/4' },
  },
  DESTINATION: {
    desktop: { width: '98px', ratio: '1/1' },
    mobile: { width: '105px', ratio: '1/1' },
  },
  PROMOTION: {
    desktop: { width: '158px', ratio: '1/1' },
    mobile: { width: '91px', ratio: '3/4' },
  },
  PROD_COMMERCIAL: {
    desktop: { width: '158px', ratio: '1/1' },
    mobile: { width: '91px', ratio: '3/4' },
  },
  PROD_EDITORIAL: {
    desktop: { width: '96px', ratio: '1/1' },
    mobile: { width: '84px', ratio: '3/4' },
  },
  PROD_FIXTURE: {
    desktop: { width: '64px', ratio: '1/1' },
    mobile: { width: '64px', ratio: '1/1' },
  },
};

const OVERLAY_CARD_PADDINGS = {
  '3_COLUMNS': '12px',
  '4_COLUMNS': '32px',
  '6_COLUMNS': '32px',
};

export const CpdsHorizontalCardContent = styled.div`
  display: flex;
  flex-direction: row;
  flex: auto;
  flex-grow: 1;
`;

export const CpdsCardContent = styled.div`
  ${() => {
    return css`
      display: flex;
      flex-direction: column;
      ${(props) => props?.styleOverrides}
    `;
  }}
`;

export const CpdsCardHoverContent = styled(CpdsCardContent)`
  position: relative;
  z-index: 1;
  opacity: 0;
  z-index: -1;
  transition: all 0.25s ease;
`;

export const CpdsCardHoverContentOverlay = styled.div`
  ${({
    theme: {
      styles: { cpds },
    },
    ...rest
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, rest?.themeColorMode);

    return css`
      position: absolute;
      content: '';
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: ${token?.card_overlay_gradient_color};
      opacity: 0.6;
      opacity: 0;
      z-index: -1;
      transition: all 0.25s ease;
      border-radius: ${token?.card_image_border_radius};
    `;
  }}
`;

export const CpdsCardCta = styled.div<{ actionDivWidth?: string }>`
  ${({ actionDivWidth }) => {
    return css`
      display: flex;
      align-items: center;
      justify-content: flex-end;
      ${checkValue(actionDivWidth)
        ? css`
            width: ${actionDivWidth} !important;
            min-width: ${actionDivWidth} !important;
            flex-shrink: unset;
          `
        : ''};
      ${(props) => props?.styleOverrides}
    `;
  }}
`;

export const CpdsCardWrapper = styled.div<CpdsCardComponentProps>`
  ${({
    theme: {
      styles: { cpds },
    },
    cardWidth = `3_COLUMNS`,
    variation = 'VERTICAL',
    bgColor = 'LIGHT',
    imageSize = 'ARTICLE',
    noLeftBorder = false,
    isHoverContent = false,
    horizontalCardHeight = '',
    cta = {},
    ...rest
  }) => {
    const { token } = themeToken(cpds, cpds?.themeColorMode, rest?.themeColorMode);

    const imageWidthRatio = checkValue(IMAGE_WIDTH[imageSize]) ? IMAGE_WIDTH[imageSize] : IMAGE_WIDTH['ARTICLE'];

    let horizontalCardWidth = '';
    let horizontalCardWidthMobile = '';
    if (cardWidth === 'FULL_WIDTH') {
      horizontalCardWidth = 'none';
      horizontalCardWidthMobile = 'none';
    } else if (cardWidth === 'HORIZONTAL_PRODUCT_FIXTURE') {
      horizontalCardWidth = token?.product_card_fixture_width;
      horizontalCardWidthMobile = token?.product_card_fixture_mobile_width;
    } else {
      horizontalCardWidth = token?.card_image_horizontal_varition_width;
      horizontalCardWidthMobile = token?.card_image_horizontal_varition_mobile_width;
    }

    const overlayCardPaddings = checkValue(OVERLAY_CARD_PADDINGS?.[cardWidth])
      ? OVERLAY_CARD_PADDINGS?.[cardWidth]
      : '12px';

    return css`
      ${variation.includes('VERTICAL')
        ? css`
            display: flex;
            flex-direction: column;
            width: 100%;
            gap: ${token?.spacing?.space_12};
            max-width: ${getCardWidth()[cardWidth]};
            ${CpdsCardContent} {
              gap: ${token?.spacing?.space_12};
            }

            @media screen and (max-width: ${cpdsMediaQueryVals.cpds_medium_upper_limit}) {
              max-width: ${getCardWidth(40, 24, 12)[cardWidth]};
            }

            @media screen and (max-width: ${cpdsMediaQueryVals.cpds_small_upper_limit}) {
              gap: ${token?.spacing?.space_8};
              max-width: ${getCardWidth(16, 8, 6)[cardWidth]};
              ${CpdsCardContent} {
                gap: ${token?.spacing?.space_8};
              }
            }
          `
        : ''}
      ${variation.includes('HORIZONTAL')
        ? css`
            position: relative;
            display: flex;
            flex-direction: row;
            column-gap: ${token?.spacing?.space_16};
            width: 100%;
            max-width: ${horizontalCardWidth};
            padding: ${token?.spacing?.space_16} ${token?.spacing?.space_20};
            background-color: ${bgColor === 'DARK'
              ? token?.card_dark_background_color
              : token?.card_light_background_color};
            cursor: auto !important;
            &:before {
              content: '';
              position: absolute;
              top: 0;
              bottom: 0;
              left: 0;
              width: 4px;
              background-color: ${token?.card_left_border_color};
            }

            &:hover {
              cursor: ${checkValue(cta) ? 'pointer' : 'default'};
            }

            ${noLeftBorder
              ? css`
                  &:before {
                    display: none;
                  }
                `
              : ``}

            ${CpdsCardFigure} {
              width: ${imageWidthRatio?.desktop?.width};
              min-width: ${imageWidthRatio?.desktop?.width};
              aspect-ratio: ${imageWidthRatio?.desktop?.ratio};
              margin: auto 0;
            }

            ${CpdsCardContent} {
              padding-top: 0;
              flex: auto;
              justify-content: center;
              flex-grow: 1;
              gap: ${token?.spacing?.space_12};
            }

            ${CpdsCardCta} {
              min-width: 105px;
              padding-left: ${token?.spacing?.space_24};
              position: relative;
              flex-shrink: 0;
              &:before {
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                width: 1px;
                background-color: ${token?.card_cta_action_border_left_color};
              }
            }

            ${cardWidth === 'HORIZONTAL_PRODUCT_FIXTURE'
              ? css`
                  padding: ${token?.spacing?.space_12} !important;
                  ${CpdsCardCta} {
                    padding-left: 14px;
                  }
                `
              : ``}

            @media screen and (max-width: ${cpdsMediaQueryVals.cpds_small_upper_limit}) {
              max-width: ${horizontalCardWidthMobile};
              padding: ${token?.spacing?.space_12} ${token?.spacing?.space_12} ${token?.spacing?.space_12}
                ${token?.spacing?.space_16};

              ${CpdsCardFigure} {
                width: ${IMAGE_WIDTH[imageSize]?.mobile?.width};
                min-width: ${IMAGE_WIDTH[imageSize]?.mobile?.width};
                aspect-ratio: ${IMAGE_WIDTH[imageSize]?.mobile?.ratio};
                margin: auto 0;
              }
              ${cardWidth !== 'HORIZONTAL_PRODUCT_FIXTURE'
                ? css`
                    ${CpdsHorizontalCardContent} {
                      flex-direction: column;
                      row-gap: ${token?.spacing?.space_16};
                      flex-grow: unset;
                      justify-content: center;
                    }
                    ${CpdsCardContent} {
                      flex-grow: unset;
                      gap: ${token?.spacing?.space_8};
                    }
                    ${CpdsCardCta} {
                      justify-content: flex-start;
                      padding-left: 0;
                      &:before {
                        display: none;
                      }
                    }
                  `
                : ''}
            }
          `
        : ''}

      ${variation === 'HORIZONTAL_DESTINATION_MAP'
        ? css`
            @media screen and (max-width: ${cpdsMediaQueryVals.cpds_small_upper_limit}) {
              flex-wrap: wrap;
              ${CpdsCardContent} {
                flex: 1;
              }
              ${CpdsCardCta} {
                width: 100%;
                margin-top: 12px;
                padding-top: 16px;
                justify-content: center;
                border-top: 1px solid #d1d5e2;
              }
            }
          `
        : ``}

      ${variation === 'LOCATION'
        ? css`
            display: flex;
            flex-direction: column-reverse;
            gap: ${token?.spacing?.space_16};
            padding-left: 14px;
            border-left: 4px solid #fff;
            max-width: 460px;
            ${CpdsCardFigure} {
              aspect-ratio: unset;
              height: 295px;
              @media screen and (max-width: ${cpdsMediaQueryVals.cpds_small_upper_limit}) {
                height: 342px;
              }
            }
            ${CpdsCardContent} {
              gap: ${token?.spacing?.space_8} !important;
            }
          `
        : ``}
        
      ${variation.includes('OVERLAY')
        ? css`
            position: relative;
            padding: 0;
            height: fit-content;

            ${CpdsCardFigure} {
              .cpds-card-component-image,
              .cpds-card-component-video {
                border-radius: 0px !important;
              }
            }

            ${isHoverContent
              ? css`
                  &:hover {
                    ${CpdsCardContent} {
                      opacity: 0;
                    }
                    ${CpdsCardHoverContent} {
                      opacity: 1;
                    }
                    ${CpdsCardHoverContentOverlay} {
                      z-index: 2;
                      opacity: 0.6;
                    }
                  }
                `
              : ``}

            ${CpdsCardContent} {
              position: absolute;
              z-index: 10;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: ${overlayCardPaddings};
              cursor: ${isHoverContent ? 'pointer' : 'default'};
              transition: all 0.25s ease;
            }

            @media screen and (max-width: ${cpdsMediaQueryVals.cpds_small_upper_limit}) {
              gap: 0;
              max-width: 358px;

              ${isHoverContent
                ? css`
                    ${CpdsCardHoverContentOverlay} {
                      opacity: 0.6 !important;
                      z-index: 2 !important;
                    }

                    ${CpdsCardContent} {
                      gap: 0;
                      opacity: 0 !important;
                      z-index: -1 !important;
                      cursor: default !important;
                      padding: 32px;
                    }
                    ${CpdsCardHoverContent} {
                      opacity: 1 !important;
                      z-index: 10 !important;
                    }
                  `
                : css``}
            }
          `
        : ``}

      ${variation === 'HORIZONTAL_OVERLAY'
        ? css`
            aspect-ratio: unset;
            ${CpdsCardContent} {
              padding: ${token?.spacing?.space_32};
            }
            ${CpdsCardFigure} {
              aspect-ratio: unset;
              width: 100%;

              ${checkValue(horizontalCardHeight)
                ? css`
                    min-height: ${horizontalCardHeight};
                    height: ${horizontalCardHeight};
                  `
                : css`
                    height: 130px;
                  `}
            }
            @media screen and (max-width: ${cpdsMediaQueryVals.cpds_small_upper_limit}) {
              aspect-ratio: 1/1;
            }
          `
        : ``}
        

      ${(props) => props?.styleOverrides}
    `;
  }}
`;
